import type { Trip, ThemeClasses, ViewType } from "../types";
import { TripGridCard } from "./TripGridCard";
import { TripListRow } from "./TripListRow";
import { DENSE_LIST } from "../../../../shared";
import type { TFunction } from "i18next";

interface TripListProps {
  trips: Trip[];
  themeClasses: ThemeClasses;
  onEdit: (trip: Trip) => void;
  onDetails: (trip: Trip) => void;
  onDelete: (trip: Trip) => void;
  /**
   * Eleva al padre la petición de abrir el modal de chat del viaje. Se
   * resuelve en `TripsSection` para que el modal sobreviva al spinner
   * full-page que esta lista pinta cuando `useTripsStore` está cargando
   * (el propio modal dispara un `fetchTrips`, ver `TripGridCard`).
   */
  onOpenChat: (trip: Trip) => void;
  selectedView: ViewType;
  t: TFunction;
  loading: boolean;
  disableCRUD: boolean;
}

export function TripList({
  trips,
  themeClasses,
  onEdit,
  onDetails,
  onDelete,
  onOpenChat,
  selectedView,
  t,
  loading,
  disableCRUD,
}: TripListProps) {
  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
      </div>
    );
  }

  if (!trips.length) {
    return (
      <div className="flex flex-col justify-center items-center py-20">
        <i className={`ri-route-line text-5xl mb-4 ${themeClasses.textSecondary}`}></i>
        <p className={`text-lg ${themeClasses.textSecondary} text-center`}>
          {t("trips.noTripsFound")}
        </p>
      </div>
    );
  }

  if (selectedView === "grid") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 px-3 sm:px-6 py-4">
        {trips.map((trip) => (
          <TripGridCard
            key={trip.id}
            trip={trip}
            themeClasses={themeClasses}
            onEdit={onEdit}
            onDetails={onDetails}
            onDelete={onDelete}
            onOpenChatModal={onOpenChat}
            t={t}
            disableCRUD={disableCRUD}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={`${DENSE_LIST} px-3 sm:px-6 py-4`}>
      {trips.map((trip) => (
        <TripListRow
          key={trip.id}
          trip={trip}
          themeClasses={themeClasses}
          onEdit={onEdit}
          onDetails={onDetails}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}
